export type BusinessHealthAnswer = 'yes' | 'no' | 'partial' | null

export interface BusinessHealthQuestion {
  id: string
  text: string
  primaryMetric?: string
}

export interface BusinessHealthGroup {
  id: string
  title: string
  description: string
  questions: BusinessHealthQuestion[]
}

export const BUSINESS_HEALTH_GROUPS: BusinessHealthGroup[] = [
  {
    id: 'team',
    title: 'Команда',
    description: 'Укомплектованная, обученная и управляемая команда обеспечивает продажи, сервис и качество тренировок.',
    questions: [
      { id: 'team.roles_closed', primaryMetric: 'Укомплектованность ключевых ролей', text: 'Закрыты ли все ключевые позиции? На одной–двух студиях — ассистент, от трёх студий — РОХ и управляющий или РОА.' },
      { id: 'team.two_administrators', text: 'Есть ли минимум два администратора?' },
      { id: 'team.teachers_cover_schedule', text: 'Хватает ли педагогов под текущее расписание (минимум три педагога на студию)?' },
      { id: 'team.teacher_attestations', primaryMetric: 'Качество и релевантность команды', text: 'У всех ли педагогов нет задолженности по аттестациям в УК?' },
      { id: 'team.admin_checklists', text: 'Выше ли 80% качество администраторов по чек-листу сервиса и продаж (шесть чек-листов на администратора, по три на каждого)?' },
      { id: 'team.values', text: 'Соответствует ли команда ценностям LEVITA: честности, эффективности и уважительному общению?' },
      { id: 'team.academy', primaryMetric: 'Обученность и введённость в стандарты', text: 'Все ли педагоги и администраторы прошли академию в УК?' },
      { id: 'team.recurring_training', text: 'Есть ли повторное обучение или дообучение сотрудников по продажам, сервису, управлению и занятиям раз в две недели или месяц?' },
      { id: 'team.onboarding', text: 'Есть ли понятный маршрут адаптации нового сотрудника и доступные сотрудникам регламенты работы?' },
      { id: 'team.regular_meetings', primaryMetric: 'Управляемость команды', text: 'Есть ли регулярные планёрки и разборы работы от руководителя отдела?' },
      { id: 'team.task_followup', text: 'Ставит ли руководитель задачи и контролирует ли их выполнение без резких выпадов и чрезмерной эмоциональности?' },
      { id: 'team.hiring_owner', text: 'Есть ли ответственный за найм и адаптацию: ассистент или рекрутер?' },
      { id: 'team.discipline', text: 'Понятна ли ответственность сотрудников за нарушения и соблюдается ли дисциплина задач, отчётов и пунктуальности?' }
    ]
  },
  {
    id: 'marketing',
    title: 'Маркетинг',
    description: 'Стабильный, управляемый и экономически эффективный поток целевых новых клиентов.',
    questions: [
      { id: 'marketing.lead_volume', primaryMetric: 'Достаточный объём целевого трафика', text: 'Не менее ли 180 заявок за период?' },
      { id: 'marketing.traffic_for_sales_plan', text: 'Хватает ли трафика под план продаж с учётом выполнения плана по лидам в прошлые месяцы и адекватности плана?' },
      { id: 'marketing.booking_rate', primaryMetric: 'Качество заявок', text: 'Выше ли 35% доходимость от заявки до записи за период?' },
      { id: 'marketing.lead_quality', text: 'Доля некачественных лидов по источникам ниже 20% за период?' },
      { id: 'marketing.agency_review', primaryMetric: 'Управление подрядчиками по таргету и каналами', text: 'Есть ли еженедельный контроль подрядчиков по плану/факту лидов, CPL и качеству?' },
      { id: 'marketing.cpl_market', text: 'Соответствует ли CPL уровню по сети LEVITA за последние месяцы?' },
      { id: 'marketing.cpl_spike', text: 'Нет ли критичного роста CPL (более 30%)?' },
      { id: 'marketing.source_concentration', text: 'Нет ли зависимости от одного источника и подрядчика? Допустимы таргет ВКонтакте и один надёжный подрядчик.' }
    ]
  },
  {
    id: 'sales',
    title: 'Продажи',
    description: 'Предсказуемая конверсия новых и действующих клиентов в выручку.',
    questions: [
      { id: 'sales.lead_response_time', primaryMetric: 'Скорость и качество обработки новых заявок', text: 'Среднее время обработки заявки меньше 15 минут?' },
      { id: 'sales.calls_quality', text: 'Выше ли 80% качество звонков и переписок по чек-листу?' },
      { id: 'sales.call_reachability', text: 'Доля дозвона по данным телефонии выше 18%?' },
      { id: 'sales.lead_to_sale', primaryMetric: 'Конверсия нового клиента по воронке', text: 'Конверсия из заявки в продажу не ниже 15% за период?' },
      { id: 'sales.average_check', primaryMetric: 'Качество продажи на студии', text: 'Соответствует ли средний чек уровню по сети LEVITA?' },
      { id: 'sales.sales_quality', text: 'Выше ли 80% качество продаж по чек-листу (шесть чек-листов на администратора, по три на каждого)?' },
      { id: 'sales.trial_followup', text: 'Выше ли 80% качество дожима после пробного занятия по чек-листу БНК?' },
      { id: 'sales.renewals_upgrades', primaryMetric: 'Работа с действующей базой', text: 'Продления и апгрейды составляют более 50% выручки за период?' },
      { id: 'sales.lost_client_returns', text: 'Не менее ли четырёх возвратов «БЧС» за период?' },
      { id: 'sales.funnel_review', primaryMetric: 'Управляемость продаж', text: 'Есть ли ежедневный или еженедельный разбор воронки, узких мест, фокусов и задач?' },
      { id: 'sales.call_feedback', text: 'Разбираются ли звонки и продажи: администраторы получают обратную связь минимум раз в неделю по трём и более примерам?' }
    ]
  },
  {
    id: 'product',
    title: 'Продукт',
    description: 'Стабильный клиентский продукт с качественным опытом и удержанием клиентов.',
    questions: [
      { id: 'product.training_quality', primaryMetric: 'Качество тренировок', text: 'Качество тренировок за период не ниже 85%?' },
      { id: 'product.secret_shopper', primaryMetric: 'Стабильность клиентского опыта', text: 'Оценка тайного покупателя не ниже 90%?' },
      { id: 'product.nps', text: 'Клиентский NPS за период не ниже 85% при минимум десяти ответах на студию?' },
      { id: 'product.ltv', text: 'Средний LTV составляет не менее семи месяцев?' },
      { id: 'product.studio_appearance', text: 'Атмосфера и внешний вид студии соответствуют регламенту?' },
      { id: 'product.timetable_grid', primaryMetric: 'Правильная структура расписания и посещаемость', text: 'Есть ли шахматка в расписании?' },
      { id: 'product.attendance', text: 'Средняя посещаемость занятий клиентами не ниже 55% за период?' },
      { id: 'product.schedule_balance', text: 'Соблюдён ли баланс популярных и классических направлений (20% классики)?' },
      { id: 'product.event_nps', primaryMetric: 'Дополнительная клиентская ценность', text: 'NPS мероприятий не ниже 80%?' }
    ]
  },
  {
    id: 'finance',
    title: 'Финансы',
    description: 'Финансово управляемая студия с контролируемыми расходами и запасом прочности.',
    questions: [
      { id: 'finance.plan_fact', primaryMetric: 'Соответствие финмодели', text: 'Ведётся ли месячный и годовой план-факт ДДС и P&L?' },
      { id: 'finance.cost_limits', text: 'Остаются ли ключевые статьи расходов в пределах допустимых отклонений?' },
      { id: 'finance.cost_review', text: 'Есть ли регулярный пересмотр расходов с поиском способов снизить затраты?' },
      { id: 'finance.owner_withdrawals', primaryMetric: 'Сбалансированность личных изъятий собственника', text: 'Не изымает ли владелец критичный объём денег из бизнеса?' },
      { id: 'finance.reserve', primaryMetric: 'Наличие финансового резерва', text: 'Есть ли на счетах резерв на три месяца покрытия точки безубыточности?' },
      { id: 'finance.week_to_week', text: 'Не живёт ли бизнес «от недели до недели» с регулярными задержками выплат?' },
      { id: 'finance.understands_reports', primaryMetric: 'Финансовая осознанность предпринимателя', text: 'Может ли франчайзи объяснить своими словами, что такое P&L и ДДС?' },
      { id: 'finance.understands_unit_economics', text: 'Может ли франчайзи объяснить экономику студии: цену лида, записи, прихода и покупки, ROMI и допустимую цену лида?' }
    ]
  },
  {
    id: 'entrepreneur',
    title: 'Предприниматель',
    description: 'Вовлечённый и устойчивый предприниматель, способный управлять и развивать студию.',
    questions: [
      { id: 'entrepreneur.management_participation', text: 'Участвует ли франчайзи в управлении компанией: проводит планёрки, принимает решения и общается с командой?' },
      { id: 'entrepreneur.levita_focus', text: 'Занимается ли франчайзи только студиями LEVITA, без других бизнесов или занятости?' },
      { id: 'entrepreneur.energy', primaryMetric: 'Энергия и ресурсное состояние', text: 'Достаточны ли субъективный уровень энергии и заинтересованность франчайзи?' },
      { id: 'entrepreneur.rest', text: 'Есть ли у франчайзи два полноценных выходных в неделю?' },
      { id: 'entrepreneur.executes_decisions', primaryMetric: 'Управленческая зрелость', text: 'Доводит ли франчайзи решения до конца и помогает ли команде выполнять задачи?' },
      { id: 'entrepreneur.not_firefighting', text: 'Не живёт ли франчайзи в постоянном пожарном режиме и понимает ли приоритеты бизнеса?' },
      { id: 'entrepreneur.handles_challenges', text: 'Сохраняет ли франчайзи устойчивость при сложностях, не уходя в хаос и избегание?' },
      { id: 'entrepreneur.role_satisfaction', primaryMetric: 'Удовлетворённость ролью и мотивация', text: 'Удовлетворён ли франчайзи своей ролью и работой, несмотря на сложности?' },
      { id: 'entrepreneur.wants_growth', text: 'Хочет ли франчайзи дальше развивать студии?' }
    ]
  }
]

export const BUSINESS_HEALTH_QUESTION_IDS = BUSINESS_HEALTH_GROUPS.flatMap(group =>
  group.questions.map(question => question.id)
)

export function emptyBusinessHealthAnswers(): Record<string, BusinessHealthAnswer> {
  return Object.fromEntries(BUSINESS_HEALTH_QUESTION_IDS.map(id => [id, null]))
}

export function normalizeBusinessHealthAnswers(value: unknown): Record<string, BusinessHealthAnswer> {
  const source = value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}
  return Object.fromEntries(BUSINESS_HEALTH_QUESTION_IDS.map(id => {
    const answer = source[id]
    return [id, answer === 'yes' || answer === 'no' || answer === 'partial' ? answer : null]
  }))
}
